import { createContext, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Plan, User } from '@back4app2/sdk';
import { toast } from 'react-hot-toast';
import back4app2 from '../back4app2';
import { DsButton, ModalDialog } from '../components/ds';
import { upgradePath } from '../containers/MyAppSettings';
import { cheapestPaidPlan, isFreePlanApp, planFeatures } from '../utils/plans';
import useContainerPurchase from './useContainerPurchase';

/**
 * What every Upgrade button in the app shell does.
 *
 * Two answers, decided by the plan the app is on:
 *
 *   Free   the checkout opens right here, with the cheapest paid plan already
 *          chosen and nothing else in the basket. Someone on Free clicking
 *          Upgrade wants the permanent URL the button promised; a plan table
 *          with a CPU column between them and the card form is a question
 *          they did not ask.
 *   paid   the Plan section of Settings, where a change is priced against the
 *          subscription the app already has — picking a size IS the decision
 *          there.
 *
 * The provider sits once in `MyApp`, so the shell renders ONE checkout modal
 * whichever button opened it. Entry points call `useUpgrade(appId)` and get a
 * function; outside the shell (no provider) it is the Settings link, which is
 * what every one of them did before.
 *
 * The purchase itself is `useContainerPurchase`, the same as the creation
 * wizard and the Plan section — `lazy`, because Settings runs the legacy plan
 * change's own Paddle on the same page, and Paddle keeps one event callback.
 */
const UpgradeContext = createContext<((appId: string) => void) | null>(null);

export const UpgradeProvider = ({ app, user, children }: {
  app: App,
  user?: User,
  children: React.ReactNode,
}) => {
  const navigate = useNavigate();
  const { purchase, checkoutModal } = useContainerPurchase({
    lazy: true,
    plainOverlay: true,
  });
  const [isWorking, setIsWorking] = useState(false);
  /** The plan that was just paid for, until the reader leaves for the Overview. */
  const [paidPlan, setPaidPlan] = useState<Plan | null>(null);

  const goToPlanSection = (appId: string) => navigate(upgradePath(appId));

  const upgrade = async (appId: string) => {
    // Another app, no account to lock the checkout to, or not on Free: the
    // Plan section answers every one of those.
    if (appId !== app.id || !user || !isFreePlanApp(app)) {
      goToPlanSection(appId);

      return;
    }

    if (isWorking) {
      return;
    }

    setIsWorking(true);

    let plan: Plan | undefined;

    try {
      plan = cheapestPaidPlan(await back4app2.getPlans());
    } catch (e) {
      console.error('could not load the plans for the upgrade', e);
    }

    if (!plan) {
      // Nothing to pre-select — the Plan section loads the table with its
      // own error handling.
      setIsWorking(false);
      goToPlanSection(appId);

      return;
    }

    const outcome = await purchase({
      source: 'upgrade_button',
      appId,
      planId: plan.id,
      extras: [],
      user,
      summary: {
        eyebrow: `upgrade to ${plan.name}`,
        planName: plan.name,
        planPrice: plan.price,
        features: planFeatures(plan),
        lines: [],
      },
    });

    setIsWorking(false);

    if (outcome.ok) {
      setPaidPlan(plan);
    } else if (outcome.reason === 'FAILED') {
      toast.error(outcome.message || "We couldn't complete the payment.");
    }
  };

  // Where the Plan section sends a buyer too: the deploy the purchase started
  // can only be watched on the Overview, and the app shell is subscribed to
  // the app, so the new plan is already on the sidebar by then.
  const onPaid = () => {
    setPaidPlan(null);
    navigate(`/apps/${app.id}`);
  };

  return (
    <UpgradeContext.Provider value={upgrade}>
      {children}
      {checkoutModal}
      <ModalDialog
        open={!!paidPlan}
        onClose={onPaid}
        title="Payment received"
        blurb="Your app is redeploying on the new plan. You can follow it on the Overview."
        footer={
          <DsButton variant="primary" onClick={onPaid}>
            Go to the Overview
          </DsButton>
        }
      />
    </UpgradeContext.Provider>
  );
};

/** The one thing an Upgrade button calls. */
export default function useUpgrade(appId: string): () => void {
  const navigate = useNavigate();
  const upgrade = useContext(UpgradeContext);

  return () => (upgrade ? upgrade(appId) : navigate(upgradePath(appId)));
}
